import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import { MessageDetail, statusBadge } from './cop-adatp3-shared.jsx';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  useTableState, SortIcon, Pagination,
} from '../../lib/table-utils.jsx';

/**
 * ADatP-3 Orders — read-only operator view of the FULL ADatP-3 inbox
 * (orders + reports, including text-only messages without map
 * coordinates). Port of reference
 * `manuals/COP-microservice-integration/ADatP-3 Orders/orders-page.jsx`.
 */

const POLL_MS = 5000;
const PAGE_SIZE = 25;

function truncate(s, n) {
  if (!s) return '';
  s = String(s).replace(/\s+/g, ' ').trim();
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export function CopOrdersPage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const isAdmin = currentUser?.role === 'admin';
  const formatDate = useCallback((iso) => iso ? formatUtcInTimezone(iso, tz) : '—', [tz]);

  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', originator: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [acksSummary, setAcksSummary] = useState({});
  const [notInstalled, setNotInstalled] = useState(null);

  const reload = useCallback(async () => {
    try {
      const m = await api.cop.adatp3.messages({ originator: filter.originator || '' });
      setAllMessages(m.messages || []);
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`Orders reload failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
    try {
      const s = await api.cop.adatp3.acksSummary();
      setAcksSummary(s.summary || {});
    } catch (err) {
      if (err.status !== 503 && err.status !== 404) {
        // eslint-disable-next-line no-console
        console.warn('[Orders] acks summary failed:', err.message);
      }
    }
  }, [filter.originator, toast]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const openDetail = useCallback(async (key) => {
    setSelected(key);
    setDetail(null);
    try {
      const data = await api.cop.adatp3.message(key);
      setDetail(data);
    } catch (err) {
      toast.error(`Load failed: ${err.message}`);
    }
  }, [toast]);

  /**
   * Per-message delete from the Orders operator view. Same backend call as
   * the ADatP-3 Bridge admin page — cascades to derived map objects in
   * cop-l-adatp3-* layers. Admin-only (button hidden for `user` role).
   */
  const handleDeleteOne = useCallback(async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(
      `Delete message ${m.key}?\n\n` +
      `Removes the message from the inbox AND any map objects derived from it.\n` +
      `Dataset is preserved; bridge will auto-create on next inbound message of the same type.`
    )) return;
    try {
      await api.cop.adatp3.remove(m.key);
      toast.success(`Deleted ${m.type || 'message'} ${m.serial || ''}`);
      if (selected === m.key) {
        setSelected(null);
        setDetail(null);
      }
      reload();
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    }
  }, [reload, toast, selected]);

  const filtered = useMemo(() => {
    return filter.type ? allMessages.filter((m) => m.type === filter.type) : allMessages;
  }, [allMessages, filter.type]);

  const searched = useMemo(
    () => applySearch(filtered, search, [
      'type', 'originator', 'serial', 'key',
      (m) => m.exercise || '',
      (m) => m.operation || '',
      (m) => m.qual || '',
      (m) => m.parseStatus || '',
    ]),
    [filtered, search]
  );

  const { pageRows, sortKey, sortDir, toggleSort, page, totalPages, setPage, total } = useTableState(searched, {
    defaultSortKey: 'receivedAt',
    defaultSortDir: 'desc',
    pageSize: PAGE_SIZE,
    sortAccessors: {
      receivedAt:    (m) => m.receivedAt || '',
      type:          (m) => m.type || '',
      serial:        (m) => parseInt(m.serial, 10) || 0,
      originator:    (m) => m.originator || '',
      periodStart:   (m) => m.periodStartIso || '',
      parseStatus:   (m) => m.parseStatus || '',
      geometryCount: (m) => m.geometryCount || 0,
      acked:         (m) => acksSummary[m.key]?.acked || 0,
    },
  });

  const counts = useMemo(() => {
    let onMap = 0, textOnly = 0, issues = 0;
    for (const m of allMessages) {
      if (m.hasGeometry || (m.geometryCount || 0) > 0) onMap++;
      else textOnly++;
      if (m.parseStatus && m.parseStatus !== 'ok') issues++;
    }
    return { total: allMessages.length, onMap, textOnly, issues };
  }, [allMessages]);

  const types       = useMemo(() => Array.from(new Set(allMessages.map((m) => m.type).filter(Boolean))).sort(), [allMessages]);
  const originators = useMemo(() => Array.from(new Set(allMessages.map((m) => m.originator).filter(Boolean))).sort(), [allMessages]);

  const exportColumns = useMemo(() => [
    { key: 'receivedAt',     label: 'Received (ISO)' },
    { key: 'type',           label: 'Type' },
    { key: 'serial',         label: 'Serial' },
    { key: 'originator',     label: 'Originator' },
    { key: 'exercise',       label: 'Exercise' },
    { key: 'operation',      label: 'Operation' },
    { key: 'qual',           label: 'Classification' },
    { key: 'periodStartIso', label: 'Period start' },
    { key: 'periodEndIso',   label: 'Period end' },
    { key: 'parseStatus',    label: 'Parse status' },
    { key: 'geometryCount',  label: 'Geometry count' },
    { key: 'amendsKey',      label: 'Amends' },
    { key: 'supersedesKey',  label: 'Supersedes' },
    { key: 'supersededBy',   label: 'Superseded by' },
    { key: 'acked',          label: 'Acked',   get: (m) => acksSummary[m.key]?.acked || 0 },
    { key: 'refused',        label: 'Refused', get: (m) => acksSummary[m.key]?.refused || 0 },
    { key: 'key',            label: 'Message key' },
  ], [acksSummary]);

  return (
    <PageShell
      icon="fa-clipboard-list"
      title="ADatP-3 Orders"
      subtitle="Full ADatP-3 inbox — orders (FRAGO/OPORD/WARNORD) and reports (INTREP/SITREP/MEDEVAC/NBC-1/CONTACTREP/…), including text-only messages without map coordinates. Read-only operator view; admin actions live on the ADatP-3 Bridge page."
      actions={
        notInstalled ? null : (
          <div className="flex items-center gap-2">
            <LastRefresh ts={refreshedAt} />
            <ExportButtons
              getRows={() => searched}
              columns={exportColumns}
              filenameBase="adatp3-orders"
              disabled={searched.length === 0}
            />
          </div>
        )
      }
    >
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="card p-10 text-center text-sm text-mist">Loading orders…</div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Total"     value={counts.total} />
            <Stat label="On map"    value={counts.onMap}    tone="emerald" />
            <Stat label="Text only" value={counts.textOnly} tone="sky" />
            <Stat label="Issues"    value={counts.issues}   tone={counts.issues > 0 ? 'amber' : undefined} />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <select className="field !w-44" value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}>
              <option value="">All types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="field !w-56" value={filter.originator} onChange={(e) => setFilter((f) => ({ ...f, originator: e.target.value }))}>
              <option value="">All originators</option>
              {originators.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <div className="min-w-[260px] flex-1 max-w-md">
              <SearchInput value={search} onChange={setSearch} placeholder="Search: type / originator / serial / exercise / operation…" />
            </div>
            <span className="ml-auto text-xs text-mist">{total} {total === 1 ? 'message' : 'messages'}{search || filter.type ? ` (of ${allMessages.length})` : ''}</span>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
                <tr>
                  <SortableTh label="Received"   sortKey={sortKey} dir={sortDir} colKey="receivedAt"    onSort={toggleSort} className="px-3 py-2.5 w-32" />
                  <SortableTh label="Type"       sortKey={sortKey} dir={sortDir} colKey="type"          onSort={toggleSort} className="px-3 py-2.5 w-24" />
                  <SortableTh label="Serial"     sortKey={sortKey} dir={sortDir} colKey="serial"        onSort={toggleSort} className="px-3 py-2.5 w-20 text-right" />
                  <SortableTh label="Originator" sortKey={sortKey} dir={sortDir} colKey="originator"    onSort={toggleSort} className="px-3 py-2.5" />
                  <SortableTh label="Period"     sortKey={sortKey} dir={sortDir} colKey="periodStart"   onSort={toggleSort} className="px-3 py-2.5 w-44" />
                  <SortableTh label="Status"     sortKey={sortKey} dir={sortDir} colKey="parseStatus"   onSort={toggleSort} className="px-3 py-2.5 w-20" />
                  <SortableTh label="Geom"       sortKey={sortKey} dir={sortDir} colKey="geometryCount" onSort={toggleSort} className="px-3 py-2.5 w-20 text-right" />
                  <SortableTh label="ACKS"       sortKey={sortKey} dir={sortDir} colKey="acked"         onSort={toggleSort} className="px-3 py-2.5 w-20 text-right" />
                  <th className="px-3 py-2.5 w-28 text-right whitespace-nowrap">Refs</th>
                  {isAdmin && <th className="px-3 py-2.5 w-10 text-center"></th>}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 10 : 9} className="p-8 text-center text-mist">
                    {search || filter.type || filter.originator ? 'No messages match the filters / search.' : 'Inbox empty.'}
                  </td></tr>
                ) : pageRows.map((m) => (
                  <tr
                    key={m.key}
                    className={`border-b border-line last:border-0 cursor-pointer hover:bg-slate-50 ${selected === m.key ? 'bg-blue-50/50' : ''} ${m.supersededBy ? 'opacity-60' : ''}`}
                    onClick={() => openDetail(m.key)}
                    title={m.supersededBy ? `Superseded by ${m.supersededBy}` : ''}
                  >
                    <td className="px-3 py-2 text-xs text-mist tabular-nums">{m.receivedAt ? formatDate(m.receivedAt) : '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-ink">{m.type || '?'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-mist">{m.serial || '—'}</td>
                    <td className="px-3 py-2 text-xs">{m.originator || '?'}</td>
                    <td className="px-3 py-2 text-xs text-mist tabular-nums" title={`${m.periodStartIso || ''} → ${m.periodEndIso || ''}`}>
                      {m.periodStartIso ? truncate(`${formatDate(m.periodStartIso)} → ${m.periodEndIso ? formatDate(m.periodEndIso) : '?'}`, 36) : <span>—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusBadge(m.parseStatus)}`}>{m.parseStatus}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {m.geometryCount > 0 ? <span className="text-emerald-700">📍 {m.geometryCount}</span> : <span className="text-mist">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      <span title="Acked / Refused">
                        <span className="font-semibold" style={{ color: '#70AF6D' }}>{acksSummary[m.key]?.acked || 0}</span>
                        <span className="text-mist">/</span>
                        <span className="text-rose-700 font-semibold">{acksSummary[m.key]?.refused || 0}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-mist text-right whitespace-nowrap">
                      {m.amendedBy?.length ? <span title="amended">⚠ {m.amendedBy.length}</span> : null}
                      {m.supersededBy ? <span className="ml-1 text-rose-600" title="superseded">🚫</span> : null}
                      {m.referencedBy?.length ? <span className="ml-1" title="referenced by">🔗 {m.referencedBy.length}</span> : null}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => handleDeleteOne(e, m)}
                          className="text-mist hover:text-rose-600"
                          title="Delete this message (and its derived map objects)"
                        >
                          <i className="fa-sharp fa-solid fa-trash text-xs" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} setPage={setPage} />
          </div>

          {detail && <MessageDetail
            detail={detail}
            tz={tz}
            onClose={() => { setDetail(null); setSelected(null); }}
            isAdmin={isAdmin}
            onReparse={isAdmin ? async () => {
              try {
                await api.cop.adatp3.reparse(selected);
                toast.success('Reparsed');
                await reload();
                const fresh = await api.cop.adatp3.message(selected);
                setDetail(fresh);
              } catch (err) { toast.error(err.message); }
            } : null}
          />}
        </>
      )}
    </PageShell>
  );
}

function SortableTh({ label, sortKey, dir, colKey, onSort, className = '' }) {
  const active = sortKey === colKey;
  return (
    <th className={`${className} cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(colKey)}>
      <span>{label}</span>
      <SortIcon active={active} dir={dir} />
    </th>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-700'
            : tone === 'amber'   ? 'text-amber-700'
            : tone === 'rose'    ? 'text-rose-700'
            : tone === 'sky'     ? 'text-sky-700'
            : 'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="text-xs uppercase tracking-wider text-mist">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
